import React, { useEffect, useMemo, useState } from 'react';
import { CalendarClock, Check, Scale, UserRoundCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { editorPublicationApi } from '@/lib/api';

const dateFromNow = (days) => {
  const value = new Date(Date.now() + days * 86400000);
  return value.toISOString().slice(0, 10);
};

export const EditorWorkloadStrip = ({ workload = {} }) => (
  <div className="mb-3 overflow-x-auto border border-border bg-card p-3" data-testid="editor-workload-balance-strip">
    <div className="mb-2 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
      <Scale className="h-3.5 w-3.5" />Weighted editor workload
    </div>
    <div className="flex min-w-max gap-2">
      {(workload.editors || []).map((editor) => (
        <div key={editor.username} className="min-w-40 border border-border px-3 py-2" data-testid={`editor-workload-${editor.username}`}>
          <div className="flex items-center justify-between gap-3"><strong className="text-[11px]">{editor.username}</strong><span className="font-mono text-[11px] text-primary">{editor.weighted_load}</span></div>
          <div className="mt-1 text-[9.5px] text-muted-foreground">{editor.active_titles} active · {editor.overdue_titles} overdue</div>
        </div>
      ))}
      {!workload.editors?.length && <div className="text-[11px] text-muted-foreground">No eligible Publishing Editors are configured.</div>}
    </div>
  </div>
);

export default function EditorAssignmentCard({ item, workload, onSaved }) {
  const [form, setForm] = useState({
    assignee_username: item.assignee_username || '',
    due_date: item.due_date || '',
    priority: item.priority || 'normal',
    reason: '',
  });
  const recommended = workload?.recommended_editor || '';
  const editors = useMemo(() => workload?.editors || [], [workload?.editors]);
  const assignedEditor = useMemo(
    () => editors.find((row) => row.username === form.assignee_username),
    [editors, form.assignee_username],
  );
  useEffect(() => {
    setForm({
      assignee_username: item.assignee_username || '',
      due_date: item.due_date || '',
      priority: item.priority || 'normal',
      reason: '',
    });
  }, [item.id, item.assignee_username, item.due_date, item.priority, item.updated_at]);

  const save = async () => {
    try {
      await editorPublicationApi.assign(item.id, {
        ...form,
        assignee_username: form.assignee_username || null,
        due_date: form.due_date || null,
      });
      toast.success(form.assignee_username ? 'Title assignment saved.' : 'Title unassigned.');
      onSaved();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Assignment could not be saved.');
    }
  };
  const applyRecommendation = () => {
    if (!recommended) return;
    setForm((current) => ({
      ...current,
      assignee_username: recommended,
      due_date: current.due_date || dateFromNow(14),
      reason: current.reason || 'Assigned using least-loaded editor recommendation.',
    }));
  };

  return (
    <div className="border border-border bg-card p-4" data-testid="editor-assignment-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[12px] font-semibold"><UserRoundCheck className="h-4 w-4 text-primary" />Editor assignment</div>
          <p className="mt-1 text-[10.5px] text-muted-foreground">Priority and overdue dates contribute to weighted workload; synced titles contribute zero.</p>
        </div>
        {recommended && <Button size="sm" variant="outline" onClick={applyRecommendation} className="h-8 gap-1.5" data-testid="editor-assignment-use-recommendation"><Check className="h-3.5 w-3.5" />Recommend {recommended}</Button>}
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <label className="text-[10px] uppercase text-muted-foreground">Publishing Editor
          <select value={form.assignee_username} onChange={(event) => setForm({ ...form, assignee_username: event.target.value })} className="mt-1 h-9 w-full border border-border bg-background px-2 text-[11.5px]" data-testid="editor-assignment-assignee"><option value="">Unassigned</option>{editors.map((row) => <option key={row.username} value={row.username}>{`${row.username} · load ${row.weighted_load}`}</option>)}</select>
        </label>
        <label className="text-[10px] uppercase text-muted-foreground">Priority
          <select value={form.priority} onChange={(event) => setForm({ ...form, priority: event.target.value })} className="mt-1 h-9 w-full border border-border bg-background px-2 text-[11.5px]" data-testid="editor-assignment-priority"><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option></select>
        </label>
        <label className="text-[10px] uppercase text-muted-foreground">Due date
          <Input type="date" value={form.due_date} onChange={(event) => setForm({ ...form, due_date: event.target.value })} className="mt-1" data-testid="editor-assignment-due-date" />
        </label>
        <label className="text-[10px] uppercase text-muted-foreground">Assignment / reassignment reason
          <Input value={form.reason} onChange={(event) => setForm({ ...form, reason: event.target.value })} placeholder="Required when reassigning" className="mt-1" data-testid="editor-assignment-reason" />
        </label>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1 text-[9.5px] uppercase text-muted-foreground"><CalendarClock className="h-3 w-3" />Due preset</span>
        {[3, 7, 14, 30].map((days) => <button key={days} type="button" onClick={() => setForm({ ...form, due_date: dateFromNow(days) })} className="border border-border px-2 py-1 text-[10px] hover:bg-secondary" data-testid={`editor-assignment-preset-${days}`}>{days} days</button>)}
        <Button size="sm" onClick={save} className="ml-auto h-8" data-testid="editor-assignment-save">Save assignment</Button>
      </div>
      {assignedEditor && <div className="mt-2 text-[10px] text-muted-foreground" data-testid="editor-assignment-selected-load">Selected load: {assignedEditor.weighted_load} points · {assignedEditor.active_titles} active · {assignedEditor.overdue_titles} overdue</div>}
      {!!item.assignment_history?.length && <div className="mt-4 overflow-auto"><table className="w-full text-[10px]" data-testid="editor-assignment-history"><thead className="uppercase text-muted-foreground"><tr><th className="px-2 py-1 text-left">When</th><th className="px-2 py-1 text-left">From</th><th className="px-2 py-1 text-left">To</th><th className="px-2 py-1 text-left">Priority / Due</th><th className="px-2 py-1 text-left">Reason</th></tr></thead><tbody>{[...item.assignment_history].reverse().map((row, index) => <tr key={`${row.at}-${index}`} className="border-t border-border"><td className="px-2 py-1 font-mono">{String(row.at || '').slice(0, 10)}</td><td className="px-2 py-1">{row.from || '—'}</td><td className="px-2 py-1">{row.to || 'Unassigned'}</td><td className="px-2 py-1">{row.priority} · {row.due_date || '—'}</td><td className="px-2 py-1">{row.reason}</td></tr>)}</tbody></table></div>}
    </div>
  );
}